import React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import Cards from '../ui/card'
import UpcomingAppointmentsCard from '../upcomingAppointmentsCard'
import useIsMobile from '../../hooks/useIsMobile'
import { Container, WelcomeHeader, WelcomeTitle, RedirectLink } from './styled'
import { Section, SectionTitle, SectionHeader, CardSection } from '../../styled/sections'
import theme from '../../styled/theme'
import { useNavigation } from '../../hooks/useNavigation'
import { useHomeLabResults } from '../../hooks/useHomeLabResults'
import { useUpcomingAppointments } from '../../hooks/useUpcomingAppointments'
import InfoCardHome from '../ui/InfoCardHome'
import InfoCard from '../infoCard'
import { LinkText } from '../ui/text'
import NewsSection from '../news'
import { PATIENT } from '../../constants'
import { BILLING_GROUP } from '../../constants/roles'
import { BILLING_PERMIT } from '../../constants/permits'
import { DIRECT_ACCES_BY_ROLE_GROUP } from '../../constants/accesByRolesGroup'
import { ROUTE_METADATA } from '../../constants/accesByRolesGroup'
import { canAccessMenuItem, getUserPermissionKeys } from '../../utils/accessControl'
import DataTable from '../DataTable'
import { addFlashMessage } from '../../actions'

const RouterLinkText = LinkText.withComponent(Link)
const MAX_APPOINTMENTS_TO_SHOW = 6

const HomeDashBoard = ({ auth, patient, addFlashMessage }) => {
	const patientId = patient?.idPaciente
	const patientERID = patient?.pacienteErid
	const handleNavigation = useNavigation()
	const { todayAppointments, upcomingAppointments } = useUpcomingAppointments({
		patientId,
		addFlashMessage
	})
	const { paginatedResults, loading, error } = useHomeLabResults({ patientERID })

	const appointmentsToShow = [
		...(todayAppointments || []),
		...(upcomingAppointments || [])
	].slice(0, MAX_APPOINTMENTS_TO_SHOW)

	const isMobile = useIsMobile()
	const textHeader = `¡Hola, ${auth?.nombre}!`

	const getDirectAccesSections = () => {
		const userRoles = auth.gruposRoles || []
		const sections = []

		userRoles.forEach(role => {
			const group = Object.keys(DIRECT_ACCES_BY_ROLE_GROUP).find(key => key === role)
			if (group && DIRECT_ACCES_BY_ROLE_GROUP[group]) {
				const accesses = DIRECT_ACCES_BY_ROLE_GROUP[group]
					.filter(item => canAccessMenuItem(item, auth))
					.filter(item => !(isMobile && item.hideOnMobileDirectAccess))
					.map(item => ({
						id: item.id,
						title: isMobile ? item.mobileLabel || item.label : item.label,
						Icon: isMobile ? item.mobileIcon || item.icon : item.icon,
						routes: item.path
					}))
				const routeMeta = ROUTE_METADATA['/inicio'] && ROUTE_METADATA['/inicio'][group]
				sections.push({
					title: routeMeta?.title || '',
					message: routeMeta?.message || '',
					list: accesses
				})
			}
		})
		return sections
	}

	const getFirstMetadata = () => {
		const userRoles = auth.gruposRoles || []
		for (let i = 0; i < userRoles.length; i++) {
			const role = userRoles[i]
			const meta = ROUTE_METADATA['/inicio'] && ROUTE_METADATA['/inicio'][role]
			if (meta) return meta
		}
		return {}
	}

	const metadata = getFirstMetadata()
	const hasBillingAccess =
		auth.gruposRoles?.includes(BILLING_GROUP) ||
		getUserPermissionKeys(auth).includes(BILLING_PERMIT)
	const billingMetadata = hasBillingAccess ? ROUTE_METADATA['/inicio']?.[BILLING_GROUP] : null

	return (
		<Container>
			<WelcomeHeader>
				<WelcomeTitle>{textHeader}</WelcomeTitle>
				{billingMetadata?.infoTitle && billingMetadata?.infoMessage && (
					<InfoCardHome
						title={billingMetadata.infoTitle}
						message={billingMetadata.infoMessage}
					/>
				)}
				{metadata?.message && <p>{metadata.message}</p>}
			</WelcomeHeader>
			{getDirectAccesSections().map((section, idx) => (
				<Section key={idx}>
					<SectionHeader align="center" directionMonbile="row" direction="row">
						<SectionTitle mbDesktop="0px">Accesos directos </SectionTitle>
					</SectionHeader>
					<CardSection>
						{auth.gruposRoles?.length > 1 && section.title && (
							<SectionTitle>{section.title}</SectionTitle>
						)}
						<Cards
							iconColor={theme.colors.brand.green}
							list={section.list}
							onClick={path => handleNavigation(path)}
						/>
					</CardSection>
				</Section>
			))}
			{auth.gruposRoles?.includes(PATIENT) && (
				<>
					<Section>
						<SectionHeader align="center" directionMonbile="row" direction="row">
							<SectionTitle mbDesktop="0px">Próximos turnos</SectionTitle>
								<RedirectLink to="/inicio/mis-turnos">
									Ir a turnos <span>›</span>
								</RedirectLink>
						</SectionHeader>
						{appointmentsToShow.length === 0 ? (
							<InfoCard
								title="No tenés turnos programados"
								subtitle={
									<RouterLinkText to="/inicio/turnos" size="14px" textDecoration="none">
										Agendar turno
									</RouterLinkText>
								}
							/>
						) : (
							<UpcomingAppointmentsCard
								appointments={appointmentsToShow}
								isCarousel={true}
								showButtons={false}
							/>
						)}
					</Section>
					<NewsSection />
					<Section>
						<SectionHeader align="center" directionMonbile="row" direction="row">
							<SectionTitle mbDesktop="0px">Resultados de laboratorio</SectionTitle>
							{paginatedResults?.length > 0 && (
								<RedirectLink to="/inicio/laboratorios">
									Ver todos <span>›</span>
								</RedirectLink>
							)}
						</SectionHeader>
						<DataTable
							hasActiveFilters={false}
							isCarousel={true}
							loading={loading}
							error={error}
							paginatedResults={paginatedResults}
							isHome={true}
						/>
					</Section>
				</>
			)}
		</Container>
	)
}

const mapStateToProps = ({ auth, patient }) => ({
	auth: auth || {},
	patient: patient || {}
})

export default connect(mapStateToProps, { addFlashMessage })(HomeDashBoard)
